import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../auth/Auth';
import { useModal } from '../../../helpers/hooks/modalContext';
import { usePayment } from '../../../helpers/hooks/PaymentContext';
import { usePaymentOptions } from '../../../helpers/hooks/PaymentOptionsContext';
import {
  buildingDetailsResponse,
  DynamicOffer,
  PaymentMethodType,
  isPaymentOptionActive,
  getPaymentOptionStatusMessage,
} from '../../../helpers/utils/types';
import {
  applyPromoCode,
  getBuildingDetailsById,
  getDynamicOffers,
  getPromoCode,
  getPadTerms,
  getTenantDetails,
} from '../../../auth/_requests';
import { Spinner } from '../../../helpers/UI/Spinner/Spinner';

import {
  getTodayDateFormatted,
  formatDateToDisplayUTCLong,
  formatFullDate,
} from '../../../helpers/utils/utils';
import { motion } from 'framer-motion';
import { ArrowRight, BadgeDollarSign, ChevronLeft, InfoIcon, Pencil, FileText } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useSubscriptionPoints } from '../../../helpers/hooks/useSubscriptionPoints';
import { ButtonSpinner } from '../../../helpers/UI/ButtonSpinner/ButtonSpinner';
import { Tooltip, Whisper } from 'rsuite';
import { showToast } from '../../../helpers/utils/showToast';
import {
  getAdvancePaymentText,
  getBusinessDays,
  numberToWord,
} from '../../../helpers/utils/paymentUtils';
import { EnumUtil } from '../../../helpers/utils/enumUtils';

interface OrderSummaryProps {
  paymentType: string;
  tenantBuildingId: string | undefined;
  onDirectPay: () => void;
  onSubscriptionPay: () => void;
  isPaying: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  paymentType,
  tenantBuildingId,
  onDirectPay,
  onSubscriptionPay,
  isPaying,
}) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { setModal } = useModal();
  const { paymentSummary } = usePayment();
  const { paymentOptions } = usePaymentOptions();
  const [buildingDetails, setBuildingDetails] = useState<buildingDetailsResponse | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [selectedDueDateTypePoints, setSelectedDueDateTypePoints] = useState<number>(0);
  const [showSwitchPopup, setShowSwitchPopup] = useState(false);
  const [promo, setPromo] = useState(''); // NOT undefined
  const [appliedPromoCode, setAppliedPromoCode] = useState('');
  const [padTermsAccepted, setPadTermsAccepted] = useState(false);
  const [padTermsContent, setPadTermsContent] = useState<string>('');
  const [isLoadingPadTerms, setIsLoadingPadTerms] = useState(false);

  // Get selected payment option and check status (all payment methods)
  const selectedPaymentOptionId = localStorage.getItem('selectedPaymentOption');
  const selectedPaymentOption = paymentOptions.find(
    (option) => option.id === selectedPaymentOptionId
  );
  const paymentMethod = EnumUtil.toEnum(selectedPaymentOption?.paymentMethod, PaymentMethodType);
  const isPAD = paymentMethod === PaymentMethodType.PreAuthorizedDebit;
  const canUsePaymentOption = isPaymentOptionActive(selectedPaymentOption?.status);
  const isPADActive = isPAD && canUsePaymentOption;
  const isPaymentOptionNotActive = !canUsePaymentOption;

  const { pointsDescription } = useSubscriptionPoints(
    auth?.data.token,
    buildingDetails?.data[0].building.rentAmount,
    tenantBuildingId,
    selectedPaymentOption?.paymentMethodId,
    selectedPaymentOption?.cardCompanyTypeId,
    paymentMethod,
    selectedPaymentOption?.businessDaysBeforeDueDate
  );
  useEffect(() => {
    if (tenantBuildingId) {
      fetchBuildingDetails();
    }
  }, [
    tenantBuildingId,
    setModal,
    selectedPaymentOption?.paymentMethodId,
    selectedPaymentOption?.cardCompanyTypeId,
  ]);

  // Fetch PAD terms when PAD is active
  useEffect(() => {
    if (isPADActive && auth?.data.token) {
      fetchPadTerms();
    } else {
      setPadTermsAccepted(false);
      setPadTermsContent('');
    }
  }, [isPADActive, paymentType, auth?.data.token, selectedPaymentOption?.id]);

  const fetchBuildingDetails = async () => {
    try {
      setLoading(true);
      const details = await getBuildingDetailsById(auth?.data?.token, tenantBuildingId);
      setBuildingDetails(details);
      if (details.data[0].tenantMitSubscription) {
        setSubscribed(true);
      }

      const offers = await getDynamicOffers(
        auth?.data?.token,
        details.data[0].building.rentAmount,
        selectedPaymentOption?.paymentMethodId,
        selectedPaymentOption?.cardCompanyTypeId
      );
      const filteredOffers = offers.data.filter((offer: DynamicOffer) => {
        const dueDateTypeName = details.data[0].dueDateType?.name;

        return offer.code === dueDateTypeName;
      });

      if (filteredOffers.length > 0) {
        setSelectedDueDateTypePoints(Number(filteredOffers[0].dynamicPoints));
      } else {
        setSelectedDueDateTypePoints(0);
      }
    } catch (e) {
      setLoading(false);
      console.error('Error fetching building details', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount || 0);
  };

  const replaceTermsVariables = (
    htmlContent: string,
    tenantName: string,
    companyName: string,
    authorizationDate: string,
    payment_amount: string,
    building_name: string
  ) => {
    return htmlContent
      .replace(/\{tenant_name\}/g, tenantName)
      .replace(/\{company_name\}/g, companyName)
      .replace(/\{authorization_date\}/g, authorizationDate)
      .replace(/\{payment_amount\}/g, payment_amount)
      .replace(/\{building_name\}/g, building_name);
  };

  const fetchPadTerms = async () => {
    if (!auth?.data.token) return;

    try {
      setIsLoadingPadTerms(true);
      setPadTermsAccepted(false);

      // Use different term names for direct vs subscription
      const termName = paymentType === 'subscription' ? 'addSubscription' : 'saleDirectPayment';

      // Fetch terms and tenant details in parallel
      const [termsResponse, tenantDetailsResponse] = await Promise.all([
        getPadTerms(auth.data.token, termName),
        getTenantDetails(auth.data.token),
      ]);

      // Parse the HTML content - handle both JSON string and regular string
      let htmlContent = termsResponse.value;
      // If it's a JSON string (starts and ends with quotes), parse it
      if (htmlContent.startsWith('"') && htmlContent.endsWith('"')) {
        try {
          htmlContent = JSON.parse(htmlContent);
        } catch {
          // If parsing fails, just remove outer quotes
          htmlContent = htmlContent.replace(/^"|"$/g, '');
        }
      }
      // Replace escaped characters
      htmlContent = htmlContent
        .replace(/\\r\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n');

      // Get tenant information for variable replacement
      const tenantDetails = tenantDetailsResponse.data;
      const firstName = tenantDetails?.user?.firstName || '';
      const lastName = tenantDetails?.user?.lastName || '';
      const tenantName = `${firstName} ${lastName}`.trim() || 'User';
      const companyName = 'TenantPay';
      const authorizationDate = formatDateToDisplayUTCLong(new Date().toISOString());
      const totalAmount =
        paymentType === 'direct'
          ? paymentSummary?.data?.directSummary?.totalAmount
          : paymentSummary?.data?.subscriptionSummary?.totalAmount;
      const payment_amount = formatCurrency(
        Number(totalAmount) || buildingDetails?.data[0].building.rentAmount || 0
      );
      const building_name = buildingDetails?.data[0].building.buildingName || '';

      // Replace variables in the HTML
      htmlContent = replaceTermsVariables(
        htmlContent,
        tenantName,
        companyName,
        authorizationDate,
        payment_amount,
        building_name
      );

      setPadTermsContent(htmlContent);
    } catch (error: any) {
      console.error('Error fetching PAD terms:', error);
      showToast({
        message: error?.message || 'Failed to load terms. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoadingPadTerms(false);
    }
  };
  useEffect(() => {
    if (buildingDetails?.data[0]?.tenantMitSubscription) {
      setSubscribed(true);
    } else {
      setSubscribed(false);
    }
  }, [buildingDetails?.data[0]?.tenantMitSubscription]);

  const handleApplyPromoCode = async () => {
    const trimmed = promo.trim();
    if (!trimmed) return;
    try {
      await applyPromoCode(auth?.data?.token, String(trimmed));
      setPromo('');
      showToast({
        message: 'Promo code applied successfully.',
        type: 'success',
      });
      // Trigger payment summary refetch in CardSection via modal flag
      setModal(null, { refetchPaymentSummary: true });
      const appliedCode = await getPromoCode(auth?.data?.token);
      setAppliedPromoCode(appliedCode?.data?.name);
    } catch (err) {
      const code = (err as Error).message;
      setPromo('');
      switch (code) {
        case 'promo_code_not_found':
          showToast({ message: 'Promo code not found.', type: 'error' });
          break;

        case 'promo_code_inactive':
          showToast({ message: 'This promo code is inactive.', type: 'error' });
          break;

        case 'promo_code_expired':
          showToast({ message: 'This promo code has expired.', type: 'error' });
          break;

        case 'promo_code_already_used':
          showToast({ message: 'You have already used this promo code.', type: 'error' });
          break;

        case 'promo_code_already_applied':
          showToast({ message: 'Promo code already applied.', type: 'error' });
          break;

        default:
          showToast({
            message: 'Something went wrong. Please try again.',
            type: 'error',
          });
      }
    }
  };
  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const appliedCode = await getPromoCode(auth?.data?.token);
        setAppliedPromoCode(appliedCode?.data?.name);
      } catch (err) {
        console.error('Failed to fetch promo code', err);
      }
    };

    fetchPromo();
  }, []);

  if (loading) {
    return <Spinner widthFull='!bg-transparent' />;
  }
  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: -5 }} // Starts slightly above
        animate={{ opacity: 1, y: 0 }} // Slides down to position
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className='flex flex-col  gap-10 xl:justify-between xl:min-h-[calc(100vh-15rem)] xl:gap-0'
      >
        <div className='flex flex-col flex-grow gap-12 w-full relative'>
          <div className='absolute top-10 -right-12 z-0 opacity-70 pointer-events-none'>
            {ellipse}
          </div>
          <div className='flex gap-2'>
            {paymentType === 'subscription' && !subscribed && (
              <ChevronLeft
                className='text-main h-7 w-7 cursor-pointer'
                onClick={() => {
                  navigate('/pay-now', {
                    state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'direct' },
                  });
                }}
              />
            )}
            <span className='text-main text-xl'>
              {paymentType == 'direct' ? 'Payment details' : 'Autopay'}
            </span>
          </div>
          {!subscribed && (
            <PaymentToggle
              isAutoPay={paymentType === 'subscription'}
              onToggle={() => {
                if (paymentType === 'subscription') {
                  if (!subscribed && Number(paymentSummary?.data?.annualSaving) > 0) {
                    setShowSwitchPopup(true);
                  } else {
                    navigate('/pay-now', {
                      state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'direct' },
                    });
                  }
                } else {
                  navigate('/pay-now', {
                    state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'autopay' },
                  });
                }
              }}
            />
          )}
          {/*  {paymentType === 'direct' &&
            !subscribed &&
            Number(paymentSummary?.data?.annualSaving) > 0 && (
              <div className='relative w-full flex items-center'>
                <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_rgba(33,81,236,0.80)] z-0 pointer-events-none'></div>
                <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-3 py-4'>
                  <div className='flex items-start gap-1'>
                    <div className='flex-1'>
                      <div className='flex gap-2 items-center'>
                        <BadgeDollarSign className='text-blue-800 mb-2' />
                        <h1 className='text-blue-800 text-lg font-semibold mb-2'>
                          {' '}
                          Save ${paymentSummary?.data?.annualSaving} in 12 months!
                        </h1>
                      </div>

                      <p className='text-sm text-blue-800 mb-3 ml-8'>
                        Switch to autopay and save $
                        {Number(paymentSummary?.data?.annualSaving) / 12} every month on processing
                        fees.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )} */}
          {showSwitchPopup && (
            <div className=' fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
              {' '}
              <div className='w-[500px] relative z-10 mt-4 rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,#FFF_-13.57%,#F2F4F9_113.63%)] px-6 py-6 shadow-lg'>
                <div className='flex gap-2 items-center mb-3'>
                  <BadgeDollarSign className='text-blue-800' />
                  <h2 className='text-blue-800 text-lg font-semibold'>
                    Save ${Number(paymentSummary?.data?.annualSaving || 0).toFixed(2)} in 12 months!
                  </h2>
                </div>
                <p className='text-sm text-blue-800 mb-5 ml-7'>
                  With autopay, you avoid extra processing fees and save around $
                  {Number(Number(paymentSummary?.data?.annualSaving ?? 0) / 12).toFixed(2)} every
                  month. Are you sure you want to switch to Pay now?
                </p>
                <div className='flex justify-end gap-3'>
                  <button
                    className='px-4 py-2 rounded-md border text-gray-600'
                    onClick={() => setShowSwitchPopup(false)}
                  >
                    Stay on autopay
                  </button>
                  <button
                    className='px-4 py-2 rounded-md bg-blue-600 text-white flex items-center gap-2'
                    onClick={() => {
                      setShowSwitchPopup(false);
                      navigate('/pay-now', {
                        state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'direct' },
                      });
                    }}
                  >
                    Switch to Pay manually
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className='relative w-full flex items-center'>
            <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_rgba(33,81,236,0.80)] z-0 pointer-events-none'></div>
            <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
              <div className='flex justify-between w-full items-center'>
                <div className='flex flex-col gap-1 w-[50%]'>
                  <span className='text-main text-base font-medium'>
                    {buildingDetails?.data[0]?.building.buildingName || 'N/A'}
                  </span>
                  <span className='text-light/70 text-sm'>
                    {buildingDetails?.data[0]?.address?.fullAddress || 'N/A'}
                  </span>
                </div>
                {paymentType === 'subscription' && (
                  <div className='px-3 py-2 rounded-[12px] border border-[rgba(63,68,83,0.10)]'>
                    <div className='flex flex-col gap-1 justify-center items-center'>
                      <div className='flex items-center gap-1'>
                        <span className='text-xs text-light/70'>
                          {subscribed ? 'Autopay date' : 'First autopay date'}
                        </span>
                        <Whisper
                          trigger='hover'
                          placement='topEnd'
                          controlId='tooltip-payment-info'
                          speaker={
                            <Tooltip>
                              Your {isPAD ? 'bank account' : 'card'} is typically charged{' '}
                              {numberToWord(
                                getBusinessDays(
                                  selectedPaymentOption?.businessDaysBeforeDueDate,
                                  paymentMethod
                                )
                              )}{' '}
                              business days before the due date to ensure your rent is paid on
                              time. If autopay is set up
                              too close to the due date, your first payment may be processed
                              slightly later than scheduled.
                            </Tooltip>
                          }
                        >
                          <button className='p-0 m-0 bg-transparent border-none cursor-pointer'>
                            <InfoIcon className='text-light/70 h-4 w-4' />
                          </button>
                        </Whisper>
                      </div>

                        <span className='text-main text-sm font-bold'>
                          {formatFullDate(
                            buildingDetails?.data[0]?.building?.nextPaymentDate ?? ''
                          ) || 'N/A'}
                        </span>
                      </div>
                    {buildingDetails?.data[0]?.building?.leaseStartDate && (
                      <div className='px-3 py-2 rounded-[12px] border border-[rgba(63,68,83,0.10)]'>
                        <div className='flex flex-col gap-1 justify-center items-center'>
                          <span className='text-xs text-light/70'>
                            Autopay start on
                          </span>
                          <span className='text-main text-sm font-bold'>
                            {formatFullDate(
                              buildingDetails.data[0].building.leaseStartDate
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {paymentType === 'direct' && !subscribed && (
            <div className='relative w-full flex items-center'>
              <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
              <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
                <div className='flex flex-col gap-3'>
                  <div>
                    {' '}
                    <span className='text-main text-base font-medium'>Earn </span>
                    <span className='text-success font-bold text-base'>
                      {' '}
                      {pointsDescription[0]?.points.replace('Earn ', '')}{' '}
                    </span>
                    <span className='text-main text-base font-medium'>every month.</span>
                  </div>
                  <div
                    className='flex flex-col px-4 py-3 items-start gap-2 rounded-[10px] bg-[rgba(33,81,236,0.08)] w-fit cursor-pointer'
                    onClick={() => {
                      setModal('subscription-points', {
                        tenantBuildingId: tenantBuildingId,
                        rentAmount: buildingDetails?.data[0].building.rentAmount,
                      });
                    }}
                  >
                    <div className='flex gap-2'>
                      <InfoIcon className='text-primary h-5 w-5' />
                      <span className='text-primary font-medium'>Tell me how</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {paymentType === 'subscription' && (
            <div className='relative w-full flex items-center'>
              <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
              <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
                <div className='flex flex-col gap-3'>
                  <div>
                    {selectedDueDateTypePoints > 0 ? (
                      <>
                        {' '}
                        <span className='text-main text-base font-medium'>
                          {subscribed ? 'Earning ' : 'Earn '}
                        </span>
                        <span className='text-success font-bold text-base'>
                          {selectedDueDateTypePoints} TP points{' '}
                        </span>
                        <span className='text-main text-base font-medium'>every month.</span>
                      </>
                    ) : (
                      <span className='text-main text-base font-medium'>
                        This payment date earns 0 points. Choose an earlier date below to start
                        earning TP points every month.
                      </span>
                    )}
                  </div>
                  <div
                    className='flex flex-col px-4 py-3 items-start gap-2 rounded-[10px] bg-[rgba(33,81,236,0.08)]  w-fit cursor-pointer'
                    onClick={() => {
                      setModal('subscription-points', {
                        tenantBuildingId: tenantBuildingId,
                        rentAmount: buildingDetails?.data[0].building.rentAmount,
                      });
                    }}
                  >
                    <div className='flex  w-full gap-5'>
                      <InfoIcon className='text-primary h-5 w-5' />
                      <span className='text-primary font-medium'>
                        Pay{' '}
                        {getAdvancePaymentText(
                          buildingDetails?.data[0]?.dueDateType?.name,
                          selectedPaymentOption?.businessDaysBeforeDueDate,
                          paymentMethod
                        )}{' '}
                        before due date every month
                      </span>

                      <Pencil
                        className='text-primary h-5 w-5'
                        onClick={() => {
                          setModal('subscription-points', {
                            tenantBuildingId: tenantBuildingId,
                            rentAmount: buildingDetails?.data[0].building.rentAmount,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {appliedPromoCode ? (
            <div className='relative w-full flex items-center'>
              {/* Shadow */}
              <div className='absolute bottom-1 left-1/2 -translate-x-1/2 w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] pointer-events-none'></div>

              {/* Card */}
              <div className='relative w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-4 py-4 flex items-center justify-between min-h-16'>
                {/* LEFT — PROMO CODE */}
                <span className='text-[16px] font-semibold tracking-wide'>
                  {appliedPromoCode.toUpperCase()}
                </span>

                {/* RIGHT — APPLIED + GREEN TICK */}
                <div className='flex items-center gap-2 text-green-600 font-medium text-[14px]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                  Applied
                </div>
              </div>
            </div>
          ) : (
            <div className='relative w-full flex items-center'>
              {/* Shadow */}
              <div className='absolute bottom-1 left-1/2 -translate-x-1/2 w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] pointer-events-none'></div>

              {/* Card */}
              <div className='relative w-full   rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-4 py-4 flex items-center gap-3'>
                <input
                  className='flex-1 bg-transparent outline-none text-[16px]'
                  placeholder='Enter promo code'
                  value={promo} // always a string
                  onChange={(e) => setPromo(e.target.value)}
                />

                <button
                  className='
    submitBtn w-[100px]
    disabled:opacity-50
    disabled:cursor-not-allowed
  '
                  onClick={handleApplyPromoCode}
                  disabled={promo.trim().length === 0}
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <div className='relative w-full flex items-center'>
            <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
            <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
              <div className='flex flex-col gap-3'>
                {paymentType === 'direct' && (
                  <div className='flex justify-between w-full items-center'>
                    <span className='text-light/70 text-base'>Payment date</span>
                    <span className='text-main font-semibold'>{getTodayDateFormatted()}</span>
                  </div>
                )}

                <div className='flex justify-between w-full items-center'>
                  <div className='flex items-center gap-2'>
                    <span className='text-light/70 text-base'>Rent amount</span>
                    <Pencil
                      className='w-4 h-4 text-light/60 hover:text-main cursor-pointer transition'
                      onClick={() =>
                        setModal('my-homes', {
                          tenantBuildingId: buildingDetails?.data[0].building.tenantBuildingId,
                        })
                      }
                    />
                  </div>

                  <span className='text-main font-semibold'>
                    {formatCurrency(buildingDetails?.data[0].building.rentAmount || 0)}
                  </span>
                </div>
                <div className='flex justify-between w-full items-center'>
                  <span className='text-light/70 text-base'>
                    {' '}
                    Processing fee{' '}
                    {paymentSummary?.data && (
                      <span>
                        {(() => {
                          const { fixed, percentage, percentageRate } =
                            paymentType === 'direct'
                              ? (paymentSummary?.data?.directSummary?.processingFee ?? {})
                              : (paymentSummary?.data?.subscriptionSummary?.processingFee ?? {});

                          if (fixed && fixed !== '0' && percentage && percentage !== '0') {
                            return ` ( ${formatCurrency(
                              Number(fixed)
                            )} + ${percentageRate}%: ${formatCurrency(Number(percentage))})`;
                          } else if (fixed && fixed !== '0') {
                            return `(${formatCurrency(Number(fixed))})`;
                          } else if (percentage && percentage !== '0') {
                            return `(${percentageRate}%: ${formatCurrency(Number(percentage))})`;
                          }
                          return null;
                        })()}
                      </span>
                    )}
                  </span>
                  <span className='text-main font-semibold'>
                    {paymentType === 'direct'
                      ? paymentSummary?.data?.directSummary?.processingFee?.total
                        ? formatCurrency(
                            Number(paymentSummary?.data?.directSummary?.processingFee?.total)
                          )
                        : '*To be calculated'
                      : paymentSummary?.data?.subscriptionSummary?.processingFee?.total
                        ? formatCurrency(
                            Number(paymentSummary?.data?.subscriptionSummary?.processingFee?.total)
                          )
                        : '*To be calculated'}
                  </span>
                </div>
                {paymentType === 'direct'
                  ? paymentSummary?.data?.directSummary?.discountAmount &&
                    Number(paymentSummary?.data?.directSummary?.discountAmount) > 0 && (
                      <div className='flex justify-between w-full items-center'>
                        <span className='text-light/70 text-base'>Rent credit</span>
                        <span className='text-success font-semibold'>
                          {formatCurrency(
                            Number(paymentSummary?.data?.directSummary?.discountAmount)
                          )}
                        </span>
                      </div>
                    )
                  : paymentSummary?.data?.subscriptionSummary?.discountAmount &&
                    Number(paymentSummary?.data?.subscriptionSummary?.discountAmount) > 0 && (
                      <div className='flex justify-between w-full items-center'>
                        <span className='text-light/70 text-base'>Rent credit</span>
                        <span className='text-success font-semibold'>
                          {formatCurrency(
                            Number(paymentSummary?.data?.subscriptionSummary?.discountAmount)
                          )}
                        </span>
                      </div>
                    )}

                {paymentType === 'direct'
                  ? paymentSummary?.data?.directSummary?.promoCodeDiscountAmount &&
                    Number(paymentSummary?.data?.directSummary?.promoCodeDiscountAmount) > 0 && (
                      <div className='flex justify-between w-full items-center'>
                        <span className='text-light/70 text-base'>Promo code discount</span>
                        <span className='text-success font-semibold'>
                          {formatCurrency(
                            Number(paymentSummary?.data?.directSummary?.promoCodeDiscountAmount)
                          )}
                        </span>
                      </div>
                    )
                  : paymentSummary?.data?.subscriptionSummary?.promoCodeDiscountAmount &&
                    Number(paymentSummary?.data?.subscriptionSummary?.promoCodeDiscountAmount) >
                      0 && (
                      <div className='flex justify-between w-full items-center'>
                        <span className='text-light/70 text-base'>Promo code discount</span>
                        <span className='text-success font-semibold'>
                          {formatCurrency(
                            Number(
                              paymentSummary?.data?.subscriptionSummary?.promoCodeDiscountAmount
                            )
                          )}
                        </span>
                      </div>
                    )}
                {paymentType === 'direct'
                  ? paymentSummary?.data?.directSummary?.totalAmount &&
                    Number(paymentSummary?.data?.directSummary?.totalAmount) > 0 && (
                      <div className='flex justify-between w-full items-center'>
                        <span className='text-light/70 text-base'>Total amount</span>
                        <span className='text-success font-semibold'>
                          {formatCurrency(Number(paymentSummary?.data?.directSummary?.totalAmount))}
                        </span>
                      </div>
                    )
                  : paymentSummary?.data?.subscriptionSummary?.totalAmount &&
                    Number(paymentSummary?.data?.subscriptionSummary?.totalAmount) > 0 && (
                      <div className='flex justify-between w-full items-center'>
                        <span className='text-light/70 text-base'>Total amount</span>
                        <span className='text-success font-semibold'>
                          {formatCurrency(
                            Number(paymentSummary?.data?.subscriptionSummary?.totalAmount)
                          )}
                        </span>
                      </div>
                    )}
              </div>
            </div>
          </div>
        </div>
        {/* {!subscribed && (
          <div className='flex items-center justify-between mb-2 mt-10 mr-1 px-4'>
            <span className='text-base font-medium text-main'>Pay now</span>
            <button
              type='button'
              onClick={() => {
                if (paymentType === 'subscription') {
                  navigate(`/payment/direct/${tenantBuildingId}`);
                } else {
                  navigate(`/payment/subscription/${tenantBuildingId}`);
                }
              }}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                 paymentType === 'direct' ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  paymentType === 'direct' ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )} */}

        <div className='flex flex-col xxs:flex-row justify-between w-full gap-10 z-10 mt-10 xl:mt-10'>
          <div className='w-full'>
            {paymentType === 'subscription' ? (
              <>
                {!subscribed ? (
                  <>
                    {/* Initial payment info */}
                    {buildingDetails?.data?.[0]?.building?.isInitialPaymentRequired && (
                      <div className='mb-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800'>
                        <p>
                          For the first month, the payment will be deducted{' '}
                          {getBusinessDays(
                            selectedPaymentOption?.businessDaysBeforeDueDate,
                            paymentMethod
                          )}{' '}
                          business days in advance.
                        </p>
                        <p className='mt-1'>
                          From the next month onward, payments will be deducted{' '}
                          {getAdvancePaymentText(
                            buildingDetails?.data?.[0]?.dueDateType?.name,
                            selectedPaymentOption?.businessDaysBeforeDueDate,
                            paymentMethod
                          )}{' '}
                          before the due date, and reward points will be awarded.
                        </p>
                      </div>
                    )}

                    {/* PAD Terms Checkbox for autopay */}
                    {isPADActive && (
                      <div className='mb-4'>
                        <div className='relative w-full flex items-center mb-3'>
                          <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
                          <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[16px]'>
                            <div className='flex items-start gap-3'>
                              <input
                                type='checkbox'
                                id='pad-terms-autopay'
                                checked={padTermsAccepted}
                                onChange={(e) => setPadTermsAccepted(e.target.checked)}
                                className='mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer'
                                disabled={isLoadingPadTerms}
                              />
                              <div className='flex-1'>
                                <label
                                  htmlFor='pad-terms-autopay'
                                  className='text-sm text-main cursor-pointer'
                                >
                                  I accept the Pre-Authorized Debit Agreement
                                </label>
                                <button
                                  type='button'
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (padTermsContent) {
                                      setModal('pad-terms', { padTermsContent });
                                    }
                                  }}
                                  className='flex items-center gap-1 text-primary text-sm mt-1 hover:underline'
                                  disabled={isLoadingPadTerms || !padTermsContent}
                                >
                                  <FileText className='h-4 w-4' />
                                  View terms
                                </button>
                              </div>
                            </div>
                            {isLoadingPadTerms && (
                              <div className='mt-2 text-sm text-light/70'>Loading terms...</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment option not active message (all methods) */}
                    {isPaymentOptionNotActive && selectedPaymentOption && (
                      <div className='mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4'>
                        <p className='text-sm font-medium text-amber-800'>
                          {getPaymentOptionStatusMessage(selectedPaymentOption.status)}
                        </p>
                        <p className='mt-1 text-xs text-amber-700'>
                          Select another payment method or add a new one to continue.
                        </p>
                      </div>
                    )}
                    {/* Confirm autopay - only when payment option is Active */}
                    {canUsePaymentOption && (
                      <button
                        className='submitBtnGreen'
                        onClick={onSubscriptionPay}
                        disabled={isPaying || (isPADActive && !padTermsAccepted)}
                      >
                        <div className='flex w-full justify-between p-4'>
                          {isPaying ? <ButtonSpinner /> : ''}
                          <span>{isPaying ? 'Processing…' : 'Confirm autopay'}</span>
                          <ArrowRight />
                        </div>
                      </button>
                    )}
                    {isPADActive && !padTermsAccepted && (
                      <p className='mt-2 text-sm text-light/70'>
                        Accept the pre-authorized debit terms above to continue.
                      </p>
                    )}
                  </>
                ) : (
                  <button
                    className='redSubmitBtn'
                    onClick={() =>
                      setModal('cancel-subscription', {
                        tenantMitSubscriptionId:
                          buildingDetails?.data?.[0]?.tenantMitSubscription
                            ?.tenantMitSubscriptionId || '',
                      })
                    }
                  >
                    <div className='flex w-full justify-between p-4'>
                      <span>Cancel autopay</span>
                      <ArrowRight />
                    </div>
                  </button>
                )}
              </>
            ) : (
              <>
                {/* PAD Terms Checkbox for Pay Now */}
                {isPADActive && (
                  <div className='mb-4'>
                    <div className='relative w-full flex items-center mb-3'>
                      <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
                      <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[16px]'>
                        <div className='flex items-start gap-3'>
                          <input
                            type='checkbox'
                            id='pad-terms-paynow'
                            checked={padTermsAccepted}
                            onChange={(e) => setPadTermsAccepted(e.target.checked)}
                            className='mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer'
                            disabled={isLoadingPadTerms}
                          />
                          <div className='flex-1'>
                            <label
                              htmlFor='pad-terms-paynow'
                              className='text-sm text-main cursor-pointer'
                            >
                              I accept the Pre-Authorized Debit Agreement
                            </label>
                            <button
                              type='button'
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (padTermsContent) {
                                  setModal('pad-terms', { padTermsContent });
                                }
                              }}
                              className='flex items-center gap-1 text-primary text-sm mt-1 hover:underline'
                              disabled={isLoadingPadTerms || !padTermsContent}
                            >
                              <FileText className='h-4 w-4' />
                              View terms
                            </button>
                          </div>
                        </div>
                        {isLoadingPadTerms && (
                          <div className='mt-2 text-sm text-light/70'>Loading terms...</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* Payment option not active message (all methods) */}
                {isPaymentOptionNotActive && selectedPaymentOption && (
                  <div className='mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4'>
                    <p className='text-sm font-medium text-amber-800'>
                      {getPaymentOptionStatusMessage(selectedPaymentOption.status)}
                    </p>
                    <p className='mt-1 text-xs text-amber-700'>
                      Select another payment method or add a new one to continue.
                    </p>
                  </div>
                )}
                {/* Pay Now - only when payment option is Active */}
                {canUsePaymentOption && (
                  <button
                    className='submitBtn'
                    onClick={onDirectPay}
                    disabled={isPaying || (isPADActive && !padTermsAccepted)}
                  >
                    <div className='flex w-full justify-between p-4'>
                      {isPaying ? <ButtonSpinner /> : ''}
                      <span>{isPaying ? 'Processing payment…' : 'Pay manually '}</span>
                      <ArrowRight />
                    </div>
                  </button>
                )}
                {isPADActive && !padTermsAccepted && (
                  <p className='mt-2 text-sm text-light/70'>
                    Accept the pre-authorized debit terms above to continue.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {paymentType === 'direct' &&
          !subscribed &&
          Number(paymentSummary?.data?.annualSaving) > 0 && (
            <span
              className='w-full items-center justify-center flex mt-3 text-[blue] underline cursor-pointer'
              onClick={() => {
                navigate('/pay-now', {
                  state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'autopay' },
                });
              }}
            >
              Switch to autopay and save $
              {Number(paymentSummary?.data?.annualSaving || 0).toFixed(2)}/year
            </span>
          )}
      </motion.section>
    </>
  );
};

export default OrderSummary;

const ellipse = (
  <svg
    width='230'
    height='544'
    viewBox='0 0 230 544'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <foreignObject x='-50' y='-50' width='644' height='644'>
      <div
        style={{
          backdropFilter: 'blur(25px)',
          clipPath: 'url(#bgblur_0_42_4387_clip_path)',
          height: '100%',
          width: '100%',
        }}
      ></div>
    </foreignObject>
    <circle
      data-figma-bg-blur-radius='50'
      cx='272'
      cy='272'
      r='272'
      fill='url(#paint0_linear_42_4387)'
      fillOpacity='0.8'
    />
    <defs>
      <clipPath id='bgblur_0_42_4387_clip_path' transform='translate(50 50)'>
        <circle cx='272' cy='272' r='272' />
      </clipPath>
      <linearGradient
        id='paint0_linear_42_4387'
        x1='102'
        y1='544'
        x2='282'
        y2='53'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#D5DBEC' />
        <stop offset='1' stopColor='white' />
      </linearGradient>
    </defs>
  </svg>
);

const PaymentToggle = ({ isAutoPay, onToggle }: { isAutoPay: boolean; onToggle: () => void }) => {
  return (
    <div className='relative w-full flex items-center '>
      <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
      <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
        <div className='flex items-center justify-between w-full'>
          <div>
            <p className='text-sm font-medium text-light/70'>Payment type</p>
          </div>

          <div className='flex items-center gap-2'>
            <span
              className={`text-sm ${!isAutoPay ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
            >
              Pay manually
            </span>
            <button
              type='button'
              onClick={onToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                isAutoPay ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                  isAutoPay ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span
              className={`text-sm ${isAutoPay ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
            >
              Autopay
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
